import type { FC } from 'react';
import { useCallback, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { Button, Text } from 'react-native-paper';

import { usePersonResendVerificationEmail } from '@/features/api/hooks/participant/usePersonResendVerificationEmail';
import { SUPPORT_URL } from '@/features/profile-setting/constants/links';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { handleOpenUrl } from '@/shared/utils/url';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    container: {
      backgroundColor: theme.colors.surfaceContainerLow,
      padding: 16,
      flexDirection: 'column',
      alignItems: 'center',
      gap: 32,
      marginTop: 16
    },
    text: { textAlign: 'center' }
  });

type EmailVerificationNeededViewProps = {
  email: string;
};

export const EmailVerificationNeededView: FC<
  EmailVerificationNeededViewProps
> = ({ email }) => {
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const { t } = useTranslation(LocalizationNameSpace.VERIFICATION, {
    keyPrefix: 'sheets.verifyMail'
  });

  const { mutate: resend, isPending } = usePersonResendVerificationEmail();

  const handleResend = useCallback(() => {
    resend();
  }, [resend]);

  return (
    <View style={styles.container}>
      <Text variant="bodyLarge" style={styles.text}>
        <Trans
          t={t}
          i18nKey="text"
          components={{
            email: <Text style={{ fontWeight: 'bold' }}>{email}</Text>
          }}
        />
      </Text>

      <Button mode="contained" disabled={isPending} onPress={handleResend}>
        {t('resend')}
      </Button>
      <Button
        mode="text"
        onPress={() => {
          handleOpenUrl(SUPPORT_URL);
        }}
      >
        {t('help')}
      </Button>
    </View>
  );
};
